import React from 'react';
import PropTypes from 'prop-types';
import { Link as UnstyledLink } from 'gatsby-plugin-react-intl';
import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';
import get from 'lodash/get';

import colors from '@utils/colors';
import { mq } from '@utils/responsive';
import useBannerContext from '@utils/useBanner';
import GlobalSearch from '@components/GlobalSearch';
import LanguageSelector from '@components/LanguageSelector';
import Wordmark from '@components/Wordmark';
import NavItems from './NavItems';

const Box = styled.div`
  display: flex;
  justify-content: center;
  transform: translateX(0);
  flex: 1 1 auto;
  //max-width: calc(50% - 130px);
  z-index: 1;

  &.logo {
    zoom: 1;
    transition: zoom 300ms cubic-bezier(0.215, 0.61, 0.355, 1);

    @media (max-width: 1075px) {
      zoom: 0.6
    }
  }


  &:first-child > * {
    margin-right: auto;
  }

  &:last-child > * {
    margin-left: auto;
  }
`;

const Inner = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: calc(1280px + 40px);
  margin: 0 auto;
  transition: padding 300ms cubic-bezier(0.215, 0.61, 0.355, 1);
`;

const SearchBox = styled.div`
  right: 0;
  display: flex;
  transition: top 300ms cubic-bezier(0.215, 0.61, 0.355, 1);
  z-index: 1;

  ${mq.gtml`
    padding: 0;
  `}
`;

const Link = styled(UnstyledLink)`
  & > svg {
    vertical-align: middle;
  }
`;

const SearchWrapper = styled.div`
  padding: 0 16px 0 24px;
`;

const Wrapper = styled(motion.nav)`
  display: flex;
  align-items: center;
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  z-index: 9998;
  background-color: transparent;
  transition: background-color 300ms cubic-bezier(0.215, 0.61, 0.355, 1);

  ${(props) =>
    props.hasScrolled &&
    css`
      background-color: ${colors.N0};
      box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.1);
    `}

  ${(props) =>
    props.hasBanner &&
    css`
      top: ${props.bannerHeight}px;
    `
  }
`;

const variants = {
  collapsed: {
    height: 80,
  },
  expanded: {
    height: 186,
  },
};

function DesktopNav({ banner, hasScrolled, navItems }) {
  const { height, viewed } = useBannerContext();

  return (
    <Wrapper
      bannerHeight={height}
      hasBanner={get(banner, 'visible') && !viewed}
      hasScrolled={hasScrolled}
      initial={{ height: 186 }}
      animate={hasScrolled ? 'collapsed' : 'expanded'}
      variants={variants}
      transition={{ ease: 'easeInOut', duration: 0.25 }}
    >
      <Inner>
        <Box>
          <NavItems items={navItems.filter((item) => item.type === 'primary')} primary />
        </Box>
        <Box isLogo={true} className="logo">
          <Link to="/" aria-label="Miss Hall's School">
            <Wordmark collapsed={hasScrolled} />
          </Link>
        </Box>
        <Box>
          <NavItems items={navItems.filter((item) => item.type === 'secondary')} primary />
        </Box>
        <SearchBox>
          <LanguageSelector />
          <SearchWrapper>
            <GlobalSearch bannerHeight={height} hasBanner={get(banner, 'visible') && !viewed} />
          </SearchWrapper>
        </SearchBox>
      </Inner>
    </Wrapper>
  );
}

DesktopNav.propTypes = {
  hasScrolled: PropTypes.bool.isRequired,
  navItems: PropTypes.array.isRequired,
};

export default DesktopNav;
